import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { DashboardBarChart, DashboardLineChart } from '../components/DashboardCharts';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatQty } from '../lib/quantity';
import { apiariesApi, type Apiary } from '../services/apiaries';
import { bottlingApi } from '../services/bottling';
import { driftplanApi, type DriftplanGrid } from '../services/driftplan';
import { extractionsApi } from '../services/extractions';
import { hivesApi, type Hive } from '../services/hives';
import { shipmentsApi, type ShipmentListItem } from '../services/orders';
import { settingsApi } from '../services/settings';

type MonthDef = {
  index: number;
  label: string;
};

const SEASON_MONTHS: readonly MonthDef[] = [
  { index: 4, label: 'Maj' },
  { index: 5, label: 'Jun' },
  { index: 6, label: 'Jul' },
  { index: 7, label: 'Aug' },
  { index: 8, label: 'Sep' },
] as const;

const ALL_MONTHS: readonly MonthDef[] = [
  { index: 0, label: 'Jan' },
  { index: 1, label: 'Feb' },
  { index: 2, label: 'Mar' },
  { index: 3, label: 'Apr' },
  { index: 4, label: 'Maj' },
  { index: 5, label: 'Jun' },
  { index: 6, label: 'Jul' },
  { index: 7, label: 'Aug' },
  { index: 8, label: 'Sep' },
  { index: 9, label: 'Okt' },
  { index: 10, label: 'Nov' },
  { index: 11, label: 'Dec' },
] as const;

type YearSeries = {
  year: number;
  values: number[];
};

type ApiaryRow = Apiary & {
  totalActiveHives: number;
  productionHives: number;
  avlaggareHives: number;
};

type DashboardState = {
  extractionSeries: YearSeries[];
  bottlingKgSeries: YearSeries[];
  bottlingUnitSeries: YearSeries[];
  shipmentUnitSeries: YearSeries[];
  totalExtractedKg: number;
  totalBottledKg: number;
  totalBottledUnits: number;
  totalShipmentUnits: number;
  apiaryRows: ApiaryRow[];
  driftplan: DriftplanGrid | null;
  beekeepingStartDate: string | null;
  currentWeekNumber: number;
  nextWeekNumber: number;
};

const emptyDashboard: DashboardState = {
  extractionSeries: [],
  bottlingKgSeries: [],
  bottlingUnitSeries: [],
  shipmentUnitSeries: [],
  totalExtractedKg: 0,
  totalBottledKg: 0,
  totalBottledUnits: 0,
  totalShipmentUnits: 0,
  apiaryRows: [],
  driftplan: null,
  beekeepingStartDate: null,
  currentWeekNumber: 0,
  nextWeekNumber: 0,
};

function parseLocalDate(value: string): Date {
  return new Date(value.replace(' ', 'T'));
}

function getIsoWeekInfo(date: Date): { isoYear: number; isoWeek: number } {
  const copy = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = copy.getUTCDay() || 7;
  copy.setUTCDate(copy.getUTCDate() + 4 - day);
  const isoYear = copy.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const isoWeek = Math.ceil((((copy.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { isoYear, isoWeek };
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = parseLocalDate(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('sv-SE');
}

function aggregateMonthlySeries<T>(
  rows: T[],
  years: number[],
  getDate: (row: T) => string | null | undefined,
  getValue: (row: T) => number | string | null | undefined,
  months: readonly MonthDef[] = SEASON_MONTHS
): YearSeries[] {
  const yearSet = new Set(years);
  const seriesByYear = new Map<number, number[]>();
  years.forEach((year) => {
    seriesByYear.set(year, Array.from({ length: months.length }, () => 0));
  });

  rows.forEach((row) => {
    const rawDate = getDate(row);
    if (!rawDate) return;
    const date = parseLocalDate(rawDate);
    if (Number.isNaN(date.getTime())) return;
    const year = date.getFullYear();
    if (!yearSet.has(year)) return;
    const monthIndex = months.findIndex((month) => month.index === date.getMonth());
    if (monthIndex < 0) return;
    const value = Number(getValue(row) ?? 0);
    if (!Number.isFinite(value)) return;
    const series = seriesByYear.get(year);
    if (!series) return;
    series[monthIndex] += value;
  });

  return years.map((year) => ({
    year,
    values: seriesByYear.get(year) ?? Array.from({ length: months.length }, () => 0),
  }));
}

function classifyHive(hive: Hive): 'produktion' | 'avlaggare' | 'other' {
  const text = `${hive.hiveType ?? ''} ${hive.hiveStatusLabel ?? ''} ${hive.hiveStatus ?? ''}`.toLowerCase();
  if (text.includes('avlägg') || text.includes('avlagg')) return 'avlaggare';
  if (text.includes('produktion')) return 'produktion';
  return 'other';
}

function getWeekRow(grid: DriftplanGrid | null, weekNumber: number) {
  return grid?.rows.find((row) => row.weekNumber === weekNumber) ?? null;
}

function MiniStatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950">{value}</div>
      {sub ? <div className="mt-1 text-sm text-slate-500">{sub}</div> : null}
    </div>
  );
}

function StatGroupCard({ items }: { items: Array<{ label: string; value: string; sub?: string }> }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item.label}>
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">{item.label}</div>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950">{item.value}</div>
            {item.sub ? <div className="mt-1 text-sm text-slate-500">{item.sub}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_14px_30px_rgba(15,23,42,0.05)]">
      <div className="mb-3">
        <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-slate-950">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

function compactWeekValue(value: string | null | undefined): string {
  if (!value) return '-';
  const trimmed = value.trim();
  return trimmed || '-';
}

function getGreeting(date: Date): string {
  const hour = date.getHours();
  if (hour >= 5 && hour < 10) return 'God morgon';
  if (hour >= 10 && hour < 14) return 'God dag';
  if (hour >= 14 && hour < 18) return 'God eftermiddag';
  if (hour >= 18 && hour < 23) return 'God kväll';
  return 'God natt';
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function addCalendarMonths(date: Date, months: number): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), 1);
  copy.setMonth(copy.getMonth() + months);
  copy.setDate(Math.min(date.getDate(), new Date(copy.getFullYear(), copy.getMonth() + 1, 0).getDate()));
  return copy;
}

function getBeekeepingDuration(startDateValue: string | null | undefined, now = new Date()) {
  const start = parseDateOnly(startDateValue);
  if (!start) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (start.getTime() > today.getTime()) {
    return {
      text: 'Startdatum framåt',
      sub: `Startar ${start.toLocaleDateString('sv-SE')}`,
    };
  }

  let years = today.getFullYear() - start.getFullYear();
  let anchor = new Date(start.getFullYear() + years, start.getMonth(), start.getDate());
  if (anchor.getTime() > today.getTime()) {
    years -= 1;
    anchor = new Date(start.getFullYear() + years, start.getMonth(), start.getDate());
  }

  let months = 0;
  while (addCalendarMonths(anchor, months + 1).getTime() <= today.getTime()) {
    months += 1;
  }
  const monthAnchor = addCalendarMonths(anchor, months);
  const days = Math.floor((today.getTime() - monthAnchor.getTime()) / 86400000);
  const totalDays = Math.floor((today.getTime() - start.getTime()) / 86400000);

  return {
    text: `${years} år, ${months} mån, ${days} dagar`,
    sub: `Totalt ${totalDays.toLocaleString('sv-SE')} dagar sedan ${start.toLocaleDateString('sv-SE')}`,
  };
}

export function HomePage() {
  const { user, canRead, loading: authLoading } = useAuth();
  const { error: toastError } = useToast();
  const navigate = useNavigate();
  const navigateYear = new Date().getFullYear();
  const isoYearInfo = useMemo(() => getIsoWeekInfo(new Date()), []);

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardState>(emptyDashboard);

  const years = useMemo(() => [navigateYear - 2, navigateYear - 1, navigateYear], [navigateYear]);

  useEffect(() => {
    if (authLoading || !user) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const extractionPromises = canRead('extractions')
          ? years.map((year) => extractionsApi.list(year).then((res) => ({ year, rows: res.extractions })))
          : [];
        const bottlingPromises = canRead('bottling')
          ? years.map((year) => bottlingApi.list(year).then((res) => ({ year, rows: res.bottlings })))
          : [];
        const shipmentPromise = canRead('orders')
          ? shipmentsApi.list().then((res) => res.shipments)
          : Promise.resolve([] as ShipmentListItem[]);
        const apiariesPromise = canRead('apiaries') ? apiariesApi.list() : Promise.resolve({ apiaries: [] as Apiary[] });
        const hivesPromise = canRead('hives') ? hivesApi.list({ includeInactive: false }) : Promise.resolve({ hives: [] as Hive[] });
        const driftplanPromise = canRead('driftplan') ? driftplanApi.getGrid(isoYearInfo.isoYear) : Promise.resolve(null);
        const companyHomeSettingsPromise = settingsApi
          .getCompanyHomeSettings()
          .catch(() => ({ beekeepingStartDate: null }));

        const [extractionsSettled, bottlingsSettled, shipmentsRes, apiariesRes, hivesRes, driftplanRes, companyHomeSettings] = await Promise.all([
          Promise.all(extractionPromises),
          Promise.all(bottlingPromises),
          shipmentPromise,
          apiariesPromise,
          hivesPromise,
          driftplanPromise,
          companyHomeSettingsPromise,
        ]);

        const extractionRows = extractionPromises.length > 0 ? extractionsSettled.flatMap((entry) => entry.rows) : [];
        const bottlingRows = bottlingPromises.length > 0 ? bottlingsSettled.flatMap((entry) => entry.rows) : [];
        const shipmentRows = shipmentsRes;

        const extractionSeries = extractionPromises.length > 0
          ? aggregateMonthlySeries(extractionRows, years, (row) => row.extractionDate, (row) => row.totalKg ?? 0)
          : [];
        const bottlingKgSeries = bottlingPromises.length > 0
          ? aggregateMonthlySeries(bottlingRows, years, (row) => row.bottlingDate, (row) => row.totalKg ?? 0, ALL_MONTHS)
          : [];
        const bottlingUnitSeries = bottlingPromises.length > 0
          ? aggregateMonthlySeries(bottlingRows, years, (row) => row.bottlingDate, (row) => row.qtyUnits ?? 0, ALL_MONTHS)
          : [];
        const shipmentUnitSeries = shipmentRows.length > 0
          ? aggregateMonthlySeries(shipmentRows, years, (row) => row.shipDate, (row) => row.totalQty ?? 0, ALL_MONTHS)
          : [];

        const activeApiaries = apiariesRes.apiaries;
        const hiveCounts = new Map<number, { production: number; avlaggare: number; active: number }>();
        hivesRes.hives.forEach((hive) => {
          if (!hive.apiarySiteId) return;
          const current = hiveCounts.get(hive.apiarySiteId) ?? { production: 0, avlaggare: 0, active: 0 };
          current.active += 1;
          const category = classifyHive(hive);
          if (category === 'produktion') {
            current.production += 1;
          } else if (category === 'avlaggare') {
            current.avlaggare += 1;
          }
          hiveCounts.set(hive.apiarySiteId, current);
        });

        const apiaryRows = activeApiaries
          .map((apiary) => {
            const counts = hiveCounts.get(apiary.id) ?? { production: 0, avlaggare: 0, active: 0 };
            return {
              ...apiary,
              totalActiveHives: counts.active,
              productionHives: counts.production,
              avlaggareHives: counts.avlaggare,
            };
          })
          .filter((apiary) => apiary.totalActiveHives > 0)
          .sort((a, b) => b.totalActiveHives - a.totalActiveHives || a.name.localeCompare(b.name, 'sv'));

        const totalExtractedKg = extractionSeries.length > 0 ? (extractionSeries[2]?.values.reduce((sum, value) => sum + value, 0) ?? 0) : 0;
        const totalBottledKg = bottlingKgSeries.length > 0 ? (bottlingKgSeries[2]?.values.reduce((sum, value) => sum + value, 0) ?? 0) : 0;
        const totalBottledUnits = bottlingUnitSeries.length > 0 ? (bottlingUnitSeries[2]?.values.reduce((sum, value) => sum + value, 0) ?? 0) : 0;
        const totalShipmentUnits = shipmentUnitSeries.length > 0 ? (shipmentUnitSeries[2]?.values.reduce((sum, value) => sum + value, 0) ?? 0) : 0;

        if (!cancelled) {
          setDashboard({
            extractionSeries,
            bottlingKgSeries,
            bottlingUnitSeries,
            shipmentUnitSeries,
            totalExtractedKg,
            totalBottledKg,
            totalBottledUnits,
            totalShipmentUnits,
            apiaryRows,
            driftplan: driftplanRes,
            beekeepingStartDate: companyHomeSettings.beekeepingStartDate,
            currentWeekNumber: isoYearInfo.isoWeek,
            nextWeekNumber: isoYearInfo.isoWeek + 1,
          });
        }
      } catch (error) {
        if (!cancelled) {
          toastError(error instanceof Error ? error.message : 'Kunde inte ladda översikten');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [authLoading, canRead, isoYearInfo.isoWeek, isoYearInfo.isoYear, toastError, user, years]);

  const currentYear = navigateYear;
  const currentExtractionSeries = dashboard.extractionSeries.find((entry) => entry.year === currentYear);
  const currentBottlingKgSeries = dashboard.bottlingKgSeries.find((entry) => entry.year === currentYear);
  const currentBottlingUnitSeries = dashboard.bottlingUnitSeries.find((entry) => entry.year === currentYear);
  const currentShipmentUnitSeries = dashboard.shipmentUnitSeries.find((entry) => entry.year === currentYear);
  const currentWeekRow = getWeekRow(dashboard.driftplan, dashboard.currentWeekNumber);
  const nextWeekRow = getWeekRow(dashboard.driftplan, dashboard.nextWeekNumber);
  const greeting = useMemo(() => getGreeting(new Date()), []);
  const beekeepingDuration = useMemo(
    () => getBeekeepingDuration(dashboard.beekeepingStartDate),
    [dashboard.beekeepingStartDate]
  );

  return (
    <div className="space-y-5">
      <section className="rounded-[30px] border border-slate-200 bg-white px-5 py-4 shadow-[0_14px_30px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Inloggad översikt</div>
            <h1 className="mt-1 text-[28px] font-semibold tracking-[-0.04em] text-slate-950 md:text-[34px]">
              {greeting}{user?.displayName ? `, ${user.displayName}` : ''}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
              Här ser du slungning, tappning, driftplan och bigårdar i en kompakt översikt.
            </p>
          </div>
          <div className="text-sm text-slate-500">
            <div>{user?.businessName || ''}</div>
            <div>Aktuell vecka: {dashboard.currentWeekNumber || isoYearInfo.isoWeek}</div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <MiniStatCard
          label="Dagar som biodlare"
          value={beekeepingDuration?.text ?? '-'}
          sub={beekeepingDuration?.sub ?? 'Sätt startdatum under Inställningar > Företagsinformation'}
        />
        <StatGroupCard
          items={[
            { label: 'Slungat', value: `${formatQty(dashboard.totalExtractedKg, 'kg')} kg`, sub: `Maj–sep ${currentYear}` },
            { label: 'Tappat kg', value: `${formatQty(dashboard.totalBottledKg, 'kg')} kg`, sub: `Jan–dec ${currentYear}` },
            { label: 'Burkar', value: formatQty(dashboard.totalBottledUnits, 'st'), sub: `Jan–dec ${currentYear}` },
            { label: 'Levererat', value: formatQty(dashboard.totalShipmentUnits, 'st'), sub: `Jan–dec ${currentYear}` },
          ]}
        />
      </div>

      <ChartCard
        title="Driftplan"
        subtitle={`Vecka ${dashboard.currentWeekNumber || isoYearInfo.isoWeek} och ${dashboard.nextWeekNumber || isoYearInfo.isoWeek + 1}`}
      >
        {!dashboard.driftplan ? (
          <p className="text-sm text-slate-500">Laddar driftplan...</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {[currentWeekRow, nextWeekRow].map((row, index) => {
              const weekNumber = index === 0 ? dashboard.currentWeekNumber : dashboard.nextWeekNumber;
              const title = index === 0 ? 'Denna vecka' : 'Nästa vecka';
              return (
                <div key={`${title}-${weekNumber}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-semibold text-slate-950">{title}</div>
                    <div className="text-sm text-slate-500">v. {weekNumber || '-'}</div>
                  </div>
                  {row ? (
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-600">{formatDate(row.mondayDate)}</div>
                      <div className="grid grid-cols-1 gap-1">
                        {dashboard.driftplan?.columns.map((column) => {
                          const value = row.values[column.key];
                          if (!value || value.trim() === '') return null;
                          return (
                            <div key={column.key} className="rounded-xl border border-white/70 bg-white px-3 py-2 text-slate-800">
                              <span className="font-medium text-slate-500">{column.header}:</span> {compactWeekValue(value)}
                            </div>
                          );
                        })}
                        {row.tasks.length > 0 ? (
                          <div className="rounded-xl border border-white/70 bg-white px-3 py-2 text-slate-800">
                            <span className="font-medium text-slate-500">Ärenden:</span>{' '}
                            {row.tasks.map((task) => task.name).join(', ')}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">Ingen rad ännu.</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ChartCard>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ChartCard
          title="Slungning per månad"
          subtitle="Maj–sep, tre års jämförelse"
        >
          {loading && dashboard.extractionSeries.length === 0 ? (
            <p className="text-sm text-slate-500">Laddar slungningsdata...</p>
          ) : currentExtractionSeries ? (
            <DashboardLineChart series={dashboard.extractionSeries} months={SEASON_MONTHS} />
          ) : (
            <p className="text-sm text-slate-500">Ingen slungningsdata finns ännu.</p>
          )}
        </ChartCard>

        <ChartCard
          title="Tappade kg per månad"
          subtitle="Jan–dec, tre års jämförelse"
        >
          {loading && dashboard.bottlingKgSeries.length === 0 ? (
            <p className="text-sm text-slate-500">Laddar tappningsdata...</p>
          ) : currentBottlingKgSeries ? (
            <DashboardLineChart series={dashboard.bottlingKgSeries} months={ALL_MONTHS} legendSuffix="" />
          ) : (
            <p className="text-sm text-slate-500">Ingen tappningsdata finns ännu.</p>
          )}
        </ChartCard>

        <ChartCard
          title="Tappade burkar per månad"
          subtitle="Jan–dec, tre års jämförelse"
        >
          {loading && dashboard.bottlingUnitSeries.length === 0 ? (
            <p className="text-sm text-slate-500">Laddar burkdata...</p>
          ) : currentBottlingUnitSeries ? (
            <DashboardBarChart series={dashboard.bottlingUnitSeries} months={ALL_MONTHS} />
          ) : (
            <p className="text-sm text-slate-500">Ingen burkdata finns ännu.</p>
          )}
        </ChartCard>

        <ChartCard
          title="Levererade burkar per månad"
          subtitle="Jan–dec, tre års jämförelse"
        >
          {loading && dashboard.shipmentUnitSeries.length === 0 ? (
            <p className="text-sm text-slate-500">Laddar leveransdata...</p>
          ) : currentShipmentUnitSeries ? (
            <DashboardBarChart series={dashboard.shipmentUnitSeries} months={ALL_MONTHS} />
          ) : (
            <p className="text-sm text-slate-500">Ingen leveransdata finns ännu.</p>
          )}
        </ChartCard>
      </div>

      <div className="md:w-1/2">
        <ChartCard
          title="Bigårdar"
          subtitle="Aktiva bigårdar med aktiva kupor per bigård"
        >
          <TableWrapper maxHeight="20rem">
            <table className="w-full border-collapse text-sm">
              <TableHead>
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Bigård</th>
                  <th className="px-2 py-1.5 text-right font-medium">Aktiva</th>
                  <th className="px-2 py-1.5 text-right font-medium">Produktion</th>
                  <th className="px-2 py-1.5 text-right font-medium">Avläggare</th>
                </tr>
              </TableHead>
              <tbody>
                {dashboard.apiaryRows.length === 0 ? (
                  <tr>
                    <td className="px-2 py-3 text-slate-500" colSpan={4}>
                      Inga bigårdar med aktiva kupor.
                    </td>
                  </tr>
                ) : (
                  dashboard.apiaryRows.map((apiary) => (
                    <tr
                      key={apiary.id}
                      className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                      onClick={() => navigate(`/apiaries/${apiary.id}`)}
                    >
                      <td className="px-2 py-1.5">
                        <div className="font-medium text-slate-950">{apiary.name}</div>
                      </td>
                      <td className="px-2 py-1.5 text-right">{apiary.totalActiveHives}</td>
                      <td className="px-2 py-1.5 text-right">{apiary.productionHives}</td>
                      <td className="px-2 py-1.5 text-right">{apiary.avlaggareHives}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrapper>
        </ChartCard>
      </div>

      <div className="text-xs text-slate-500">
        Dashboarden visar tre års säsongsdata för maj–sep, driftplanens aktuella veckor och aktiva bigårdar.
      </div>
    </div>
  );
}


